/*eslint max-lines-per-function: ["error", 265]*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';

import { useTranslation } from '../../../translations';
import {
  GetStageHintDto,
  ChecklistItemDto,
  StepErrorDtoLevelEnum,
  ResponseError,
  StepErrorDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { TimelineStepEnum } from '../../../common/pokCore/validation/enums';

import { StageModalFilters } from './StageModalFilters';
import { StageModalChecklist, ChecklistItem } from './StageModalChecklist';
import { StageModalHints } from './StageModalHints';
import { StageModalErrors } from './StageModalErrors';
import { StageModalInfo } from './StageModalInfo';
import { StageModalRequests } from './StageModalRequests';
import { Stage } from './TimeLine';

interface StageModalProps {
  stage: Stage;
  show: boolean;
  onClose: () => void;
  projectId: string;
  monthsOptions: { value: string | undefined; label: string }[];
  selectedMonth: string | undefined;
}

const convertChecklistItem = (item: ChecklistItemDto): ChecklistItem => ({
  stepDefinitionId: item.stepDefinitionId,
  text: item.statusMessage || '',
  completed: item.completed,
  auto: item.automatic,
  type: item.stepType as TimelineStepEnum,
  additionalInfo: item.additionalInfo,
});

function useAnimatedHeight(show: boolean) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!show) {
      setContentHeight(undefined);
      return;
    }
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(entries => {
      setContentHeight(entries[0].contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [show]);
  return { contentRef, contentHeight };
}

export const StageModal: React.FC<StageModalProps> = ({
  stage,
  show,
  onClose,
  projectId,
  monthsOptions,
  selectedMonth,
}) => {
  const [selectedProfile, setSelectedProfile] = useState<string | undefined>(
    undefined,
  );
  const [stageSelectedMonth, setStageSelectedMonth] = useState<
    string | undefined
  >(selectedMonth);
  const [hints, setHints] = useState<GetStageHintDto[]>([]);
  const [loadingHints, setLoadingHints] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [errorList, setErrorList] = useState<StepErrorDto[]>([]);
  const [loadingErrors, setLoadingErrors] = useState(false);

  const { contentRef, contentHeight } = useAnimatedHeight(show);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('projectLng');
  const Icon = stage.icon;

  const isLoading = loadingErrors || loadingHints;

  const refreshErrors = useCallback(async () => {
    setLoadingErrors(true);
    try {
      const response = await pok.stepDefinitions.getErrorList(
        projectId,
        stage.id,
        selectedProfile,
        stageSelectedMonth ? new Date(stageSelectedMonth) : undefined,
      );
      setErrorList(response.errors);
    } catch (errorResponse) {
      notifications.caughtError(errorResponse as ResponseError);
    } finally {
      setLoadingErrors(false);
    }
  }, [
    notifications,
    pok.stepDefinitions,
    projectId,
    selectedProfile,
    stage.id,
    stageSelectedMonth,
  ]);

  const refreshChecklist = useCallback(async () => {
    if (!selectedProfile || !stageSelectedMonth) {
      setChecklist([]);
      return;
    }
    setLoadingChecklist(true);
    try {
      const response = await pok.stepDefinitions.getChecklist(
        projectId,
        stage.id,
        selectedProfile,
        new Date(stageSelectedMonth),
      );
      setChecklist(response.items.map(convertChecklistItem));
    } catch (errorResponse) {
      notifications.caughtError(errorResponse as ResponseError);
    } finally {
      setLoadingChecklist(false);
    }
  }, [
    notifications,
    pok.stepDefinitions,
    projectId,
    selectedProfile,
    stage.id,
    stageSelectedMonth,
  ]);

  useEffect(() => {
    if (!show) {
      return;
    }
    setLoadingHints(true);
    pok.stageHints
      .getHintsForStage(stage.id)
      .then(setHints)
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setLoadingHints(false));
  }, [notifications, pok.stageHints, show, stage.id]);

  useEffect(() => {
    if (!show) {
      setChecklist([]);
      return;
    }
    void refreshChecklist();
  }, [refreshChecklist, show]);

  useEffect(() => {
    if (!show) {
      setErrorList([]);
      return;
    }
    void refreshErrors();
  }, [refreshErrors, show]);

  useEffect(() => {
    if (!show) {
      setSelectedProfile(undefined);
      return;
    }
    pok.profiles
      .getAllForProject(projectId)
      .then(profiles => {
        if (profiles.length === 1) {
          setSelectedProfile(profiles[0].id);
        } else {
          setSelectedProfile(undefined);
        }
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });
  }, [notifications, pok.profiles, projectId, show]);

  const onNoMediaplanChange = useCallback(
    (stepDefinitionId: string, completed: boolean) => {
      const dto = {
        projectId: projectId,
        stageDefinitionId: stage.id,
        profileId: selectedProfile,
        stepDefinitionId: stepDefinitionId,
        month: stageSelectedMonth ? new Date(stageSelectedMonth) : undefined,
        completed: completed,
      };
      pok.stepDefinitions
        .toggleManualStepCompletion(dto)
        .then(async () => {
          await Promise.all([refreshChecklist(), refreshErrors()]);
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    },
    [
      projectId,
      stage.id,
      selectedProfile,
      stageSelectedMonth,
      pok.stepDefinitions,
      refreshChecklist,
      refreshErrors,
      notifications,
    ],
  );

  const onContractRequiredChange = useCallback(
    async (estimateItemId: string, checked: boolean) => {
      try {
        await pok.stepDefinitions.setNoOrderRequired(estimateItemId, checked);
        await refreshChecklist();
        await refreshErrors();
      } catch (errorResponse) {
        notifications.caughtError(errorResponse as ResponseError);
      }
    },
    [pok.stepDefinitions, refreshChecklist, refreshErrors, notifications],
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      closeButton
      backdrop="static"
      dialogClassName="stage-modal-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <div className={`stage-modal-icon-circle stage-modal-icon-circle`}>
            <Icon size={20} />
          </div>
          {stage.label}
          {isLoading && <Spinner size="sm" className="ms-2 text-secondary" />}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div
          style={{
            height: contentHeight !== undefined ? contentHeight : 'auto',
            overflow: 'hidden',
            transition: 'height 0.3s ease',
          }}
        >
          <div
            ref={contentRef}
            className={`d-flex flex-column gap-4${isLoading ? ' stage-modal-body-loading' : ''}`}
          >
            <StageModalFilters
              monthsOptions={monthsOptions}
              projectId={projectId}
              selectedMonth={stageSelectedMonth}
              selectedProfile={selectedProfile}
              onMonthChange={setStageSelectedMonth}
              onProfileChange={profile => setSelectedProfile(profile)}
            />
            <StageModalErrors
              errors={errorList.filter(
                e =>
                  e.level === StepErrorDtoLevelEnum.Error ||
                  e.level === StepErrorDtoLevelEnum.Warning,
              )}
            />
            <StageModalInfo
              items={errorList.filter(
                e =>
                  e.level === StepErrorDtoLevelEnum.Info ||
                  e.level === StepErrorDtoLevelEnum.Future,
              )}
            />
            <StageModalRequests
              items={errorList.filter(
                e =>
                  e.level === StepErrorDtoLevelEnum.RequestInfo ||
                  e.level === StepErrorDtoLevelEnum.RequestError,
              )}
            />
            {selectedProfile && stageSelectedMonth && checklist.length > 0 && (
              <StageModalChecklist
                items={checklist}
                loading={loadingChecklist}
                onNoMediaplanChange={onNoMediaplanChange}
                onContractRequiredChange={onContractRequiredChange}
              />
            )}
            <StageModalHints hints={hints} />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button className="btn btn-outline-secondary" onClick={onClose}>
          {t(tk.button.close)}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
